/**
 * The reporting centre.
 *
 * Every report is produced by a named engine on the server from posted
 * transactions, and every one carries a header saying who ran it, when, against
 * which parameters, and that the data is synthetic. A report printed and taken
 * into a meeting should be able to answer "where did this come from?" on its
 * own face. The purchase- and sales-ledger ageing routers return no header of
 * their own, so this screen composes one from the catalogue, the session and
 * the tenant rather than leaving those pages unattributed.
 *
 * Each report is described once, in REPORTS below: which endpoint runs it,
 * which parameters it needs, and exactly which columns a reader should see.
 * Nothing is derived from whatever keys the query happened to select — doing
 * that is what produced run-together headings, ageing buckets printed as JSON
 * and a dozen columns running off the side of a projector.
 *
 * Figures drill: a trial balance line opens the transactions behind it, a
 * transaction opens its journal, and the journal opens the invoice and the
 * scanned document. That chain is why the reporting screens link rather than
 * merely display.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTenant, useTerms, type Terminology } from '../lib/tenant';
import { date, dateTime, humanise, number, percent, statusLabel, statusTone, todayIso } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorNotice,
  Money,
  PageHeader,
  Select,
  Stat,
  type Column,
} from '../components/ui';

interface ReportDefinition {
  code: string;
  name: string;
  description: string;
  category: string;
  engine: string;
  required_permission: string | null;
  supports_drilldown: boolean;
}

/* ------------------------------------------------------------------ *
 * Reading a payload the server sent
 * ------------------------------------------------------------------ */

/** One row of a report, straight from the API. Values are read, never trusted. */
type Row = Record<string, unknown>;
type Payload = Record<string, unknown>;

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function rowsOf(data: Payload, key: string): Row[] {
  const value = data[key];
  return Array.isArray(value) ? (value as Row[]) : [];
}

function objectOf(data: Payload, key: string): Payload {
  const value = data[key];
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Payload) : {};
}

/**
 * A lower-case token with no spaces is a database enum, not prose.
 *
 * "local_authority" and "trade" both arrive as data in columns whose other
 * values are free text, so the safety net is applied at the point of render
 * rather than by listing every enum column by hand.
 */
const MACHINE_TOKEN = /^[a-z][a-z0-9_]*$/;

function humaniseIfMachine(value: string): string {
  return MACHINE_TOKEN.test(value) ? statusLabel(value) : value;
}

/* ------------------------------------------------------------------ *
 * Columns and sections
 * ------------------------------------------------------------------ */

type CellKind = 'text' | 'code' | 'label' | 'status' | 'money' | 'number' | 'percent' | 'date' | 'list';

interface ColSpec {
  key: string;
  header: string;
  kind?: CellKind;
  width?: string;
  sortable?: boolean;
  /** Blank rather than "£0.00" — for ageing buckets and trial balance columns. */
  zeroBlank?: boolean;
  /** Where this cell leads. Returning null leaves the cell as plain text. */
  linkTo?: (row: Row) => string | null;
  /** Indent by the row's own depth, so a hierarchy reads as one. */
  indent?: boolean;
  /** Bespoke rendering where a kind is not enough. */
  render?: (row: Row) => ReactNode;
}

interface Section {
  key: string;
  title: string;
  subtitle?: string;
  columns: ColSpec[];
  rows: Row[];
  /** A totals line under the table, built by the report from server figures. */
  total?: { label: string; cells: Record<string, ReactNode> };
  /** Rows that are subtotals rather than postings, shown in bold. */
  emphasise?: (row: Row) => boolean;
  searchable?: boolean;
  pageSize?: number;
  empty?: string;
}

interface StatSpec {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'positive' | 'negative';
  to?: string;
  drillLabel?: string;
}

interface ReportView {
  stats?: StatSpec[];
  /** Anything that must be said before the figures — a control note, a caveat. */
  notes?: ReactNode;
  sections: Section[];
  /** What the report was run for, when the server sends no parameter line. */
  parameters?: string | null;
}

function isNumericKind(kind: CellKind | undefined): boolean {
  return kind === 'money' || kind === 'number' || kind === 'percent';
}

function renderCell(row: Row, col: ColSpec, strong: boolean): ReactNode {
  if (col.render) return col.render(row);

  const raw = row[col.key];
  const kind = col.kind ?? 'text';

  let body: ReactNode;
  if (kind === 'money') {
    body = <Money minor={num(raw)} blankIfZero={col.zeroBlank} bold={strong} />;
  } else if (kind === 'number') {
    const n = num(raw);
    body = n === null ? <span className="muted">—</span> : number(n);
  } else if (kind === 'percent') {
    body = percent(num(raw));
  } else if (kind === 'date') {
    body = date(text(raw));
  } else if (kind === 'list') {
    const items = Array.isArray(raw) ? raw.map((v) => text(v)).filter((v): v is string => v !== null) : [];
    body = items.length > 0 ? items.join(', ') : <span className="muted">—</span>;
  } else if (kind === 'status') {
    const value = text(raw);
    body = value === null ? <span className="muted">—</span> : <Badge tone={statusTone(value)}>{statusLabel(value)}</Badge>;
  } else {
    const value = text(raw);
    if (value === null) {
      body = <span className="muted">—</span>;
    } else if (kind === 'code') {
      body = <span className="mono smaller">{value}</span>;
    } else if (kind === 'label') {
      body = statusLabel(value);
    } else {
      body = humaniseIfMachine(value);
    }
  }

  const href = col.linkTo ? col.linkTo(row) : null;
  if (href) body = <Link to={href}>{body}</Link>;

  if (col.indent) {
    const depth = num(row.indentLevel) ?? num(row.depth) ?? 0;
    body = (
      <span style={depth > 0 ? { paddingLeft: `${depth * 1.1}rem`, display: 'inline-block' } : undefined}>{body}</span>
    );
  }

  if (strong && kind !== 'money') return <span className="strong">{body}</span>;
  return body;
}

function sortValueFor(row: Row, col: ColSpec): string | number | null {
  const raw = row[col.key];
  if (isNumericKind(col.kind)) return num(raw);
  if (col.kind === 'date') {
    const value = text(raw);
    return value === null ? null : value.slice(0, 10);
  }
  return text(raw);
}

/* ------------------------------------------------------------------ *
 * Export
 * ------------------------------------------------------------------ */

/**
 * Flatten a cell for the CSV.
 *
 * Money stays an integer count of pence because the server's exporter turns it
 * into a decimal Excel will treat as a number; everything else becomes the same
 * text the reader saw on screen, so dates leave the product in UK form and an
 * ageing bucket never arrives in a spreadsheet as "[object Object]".
 */
function exportValue(row: Row, col: ColSpec): string | number | null {
  const raw = row[col.key];
  if (col.kind === 'money') return num(raw);
  if (col.kind === 'number' || col.kind === 'percent') return num(raw);
  if (col.kind === 'date') {
    const value = text(raw);
    return value === null ? '' : date(value);
  }
  if (col.kind === 'list') {
    return Array.isArray(raw) ? raw.map((v) => text(v) ?? '').filter(Boolean).join('; ') : '';
  }
  const value = text(raw);
  if (value === null) return '';
  if (col.kind === 'label' || col.kind === 'status') return statusLabel(value);
  return humaniseIfMachine(value);
}

function exportTypeFor(kind: CellKind | undefined): 'text' | 'money' | 'number' | 'date' {
  if (kind === 'money') return 'money';
  if (kind === 'number' || kind === 'percent') return 'number';
  return 'text';
}

/* ------------------------------------------------------------------ *
 * Ageing buckets
 * ------------------------------------------------------------------ */

/**
 * The ageing analysis IS the aged creditors and aged debtors report, so each
 * bucket earns a money column of its own. The purchase ledger sends buckets as
 * an array of objects and the sales ledger as a keyed object; both are folded
 * into the same flat row here so the two reports read identically.
 */
const AGEING = [
  { source: 'current', key: 'ageCurrent', header: 'Not yet due' },
  { source: '1-30', key: 'age1to30', header: '1–30 days' },
  { source: '31-60', key: 'age31to60', header: '31–60 days' },
  { source: '61-90', key: 'age61to90', header: '61–90 days' },
  { source: '90+', key: 'age90plus', header: 'Over 90 days' },
] as const;

function ageingColumns(): ColSpec[] {
  return AGEING.map((b) => ({ key: b.key, header: b.header, kind: 'money' as const, zeroBlank: true, sortable: true }));
}

function bucketsFromArray(value: unknown): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const list = Array.isArray(value) ? (value as Row[]) : [];
  for (const bucket of AGEING) {
    const found = list.find((b) => text(b.label) === bucket.source);
    out[bucket.key] = found ? num(found.amountMinor) : null;
  }
  return out;
}

function bucketsFromObject(value: unknown): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const source = value !== null && typeof value === 'object' ? (value as Row) : {};
  for (const bucket of AGEING) {
    out[bucket.key] = num(source[bucket.source]);
  }
  return out;
}

/** The payables forecast names its buckets in machine form; these are the words. */
const CASH_REQUIREMENT_LABELS: Record<string, string> = {
  overdue: 'Already overdue',
  '1-7 days': 'Within 7 days',
  '8-14 days': '8 to 14 days',
  '15-30 days': '15 to 30 days',
  '31-60 days': '31 to 60 days',
  '60+ days': 'More than 60 days',
};

function ageingTotalCells(totals: unknown, grandTotalMinor: number | null): Record<string, ReactNode> {
  const cells: Record<string, ReactNode> = {};
  const mapped = bucketsFromArray(totals);
  for (const bucket of AGEING) {
    cells[bucket.key] = <Money minor={mapped[bucket.key] ?? null} blankIfZero bold />;
  }
  cells.totalMinor = <Money minor={grandTotalMinor} bold />;
  return cells;
}

/* ------------------------------------------------------------------ *
 * What each report needs, and what it shows
 * ------------------------------------------------------------------ */

/** Which pickers a report needs above its table. */
type ParamKind = 'none' | 'period' | 'periodAndCostCentre' | 'year' | 'scope';

interface RunParams {
  periodId: string | null;
  financialYearId: string | null;
  costCentreId: string | null;
  /** Filters inherited from a drill-down elsewhere in the product. */
  accountId: string | null;
  projectId: string | null;
  /** 'period' or 'year' — how the general ledger enquiry is currently cut. */
  scope: 'period' | 'year';
}

interface BuildContext {
  t: Terminology;
  params: RunParams;
  yearName: string | null;
  periodName: string | null;
}

interface ReportSpec {
  params: ParamKind;
  /** Run the report. A spec may make more than one request to do it properly. */
  run: (params: RunParams) => Promise<Payload>;
  build: (data: Payload, ctx: BuildContext) => ReportView;
  /** Reports that belong to a screen of their own rather than to this runner. */
  openIn?: undefined;
}

/** A report the catalogue offers that is really a screen elsewhere. */
interface ReportRedirect {
  openIn: string;
}

type ReportEntry = ReportSpec | ReportRedirect;

function isRedirect(entry: ReportEntry): entry is ReportRedirect {
  return typeof (entry as ReportRedirect).openIn === 'string';
}

function queryString(pairs: Array<[string, string | null]>): string {
  const params = new URLSearchParams();
  for (const [key, value] of pairs) if (value) params.set(key, value);
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Where the transactions behind a figure live. */
function ledgerEnquiry(pairs: Array<[string, string | null]>): string {
  return `/reports/GENERAL_LEDGER${queryString(pairs)}`;
}

const BUDGET_COLUMNS = (t: Terminology): ColSpec[] => [
  { key: 'annualBudgetMinor', header: 'Annual budget', kind: 'money', sortable: true },
  { key: 'budgetToDateMinor', header: 'Budget to date', kind: 'money' },
  {
    key: 'actualToDateMinor',
    header: 'Actual to date',
    kind: 'money',
    sortable: true,
    linkTo: (row) => ledgerEnquiry([['costCentreId', text(row.costCentreId)], ['accountId', text(row.accountId)]]),
  },
  { key: 'commitmentMinor', header: `Committed on ${lower(t.purchaseOrderPlural)}`, kind: 'money', zeroBlank: true },
  { key: 'availableMinor', header: 'Available', kind: 'money' },
  { key: 'utilisationPercent', header: 'Used', kind: 'percent', width: '5.5rem' },
];

function budgetTotalCells(totals: Payload): Record<string, ReactNode> {
  return {
    annualBudgetMinor: <Money minor={num(totals.annualBudgetMinor)} bold />,
    budgetToDateMinor: <Money minor={num(totals.budgetToDateMinor)} bold />,
    actualToDateMinor: <Money minor={num(totals.actualToDateMinor)} bold />,
    commitmentMinor: <Money minor={num(totals.commitmentMinor)} blankIfZero bold />,
    availableMinor: <Money minor={num(totals.availableMinor)} bold />,
    utilisationPercent: <span className="strong">{percent(num(totals.utilisationPercent))}</span>,
  };
}

/** The income and expenditure table, shared by three reports that all print it. */
function incomeExpenditureSection(ie: Payload, key: string, title: string): Section {
  const lines = rowsOf(ie, 'lines');
  // Prior-year comparatives are only shown when the ledger actually holds them;
  // a column of blanks beside real figures reads as a broken report.
  const hasPriorYear = lines.some((l) => (num(l.priorYearToDateMinor) ?? 0) !== 0);
  const hasBudget = lines.some((l) => (num(l.budgetYearToDateMinor) ?? 0) !== 0);

  const columns: ColSpec[] = [
    {
      key: 'accountCode',
      header: 'Code',
      width: '4.75rem',
      // Subtotal and heading lines have no nominal code, and a column of
      // em-dashes down the middle of a set of accounts reads as missing data.
      render: (row) => {
        const code = text(row.accountCode);
        return code ? <span className="mono smaller">{code}</span> : null;
      },
    },
    {
      key: 'accountName',
      header: 'Account',
      indent: true,
      linkTo: (row) => {
        const drill = objectOf(row, 'drillTo');
        const accountId = text(drill.accountId);
        return accountId ? ledgerEnquiry([['accountId', accountId], ['periodId', text(drill.periodId)]]) : null;
      },
    },
    { key: 'periodMinor', header: 'This period', kind: 'money', zeroBlank: true },
    { key: 'yearToDateMinor', header: 'Year to date', kind: 'money', zeroBlank: true },
  ];
  if (hasBudget) {
    columns.push({ key: 'budgetYearToDateMinor', header: 'Budget to date', kind: 'money', zeroBlank: true });
    columns.push({ key: 'varianceMinor', header: 'Variance', kind: 'money', zeroBlank: true });
  }
  if (hasPriorYear) {
    columns.push({ key: 'priorYearToDateMinor', header: 'Prior year to date', kind: 'money', zeroBlank: true });
  }

  return {
    key,
    title,
    columns,
    rows: lines,
    emphasise: (row) => row.isSubtotal === true,
  };
}

const REPORTS: Record<string, ReportEntry> = {
  /* ---------------- Financial statements ---------------- */

  TRIAL_BALANCE: {
    params: 'period',
    run: (p) => api.get<Payload>(`/reports/trial-balance${queryString([['periodId', p.periodId]])}`),
    build: (data, ctx) => {
      const rows = rowsOf(data, 'rows');
      const balanced = data.isBalanced === true;
      return {
        stats: [
          { label: 'Total debits', value: <Money minor={num(data.totalDebitMinor)} /> },
          { label: 'Total credits', value: <Money minor={num(data.totalCreditMinor)} /> },
          {
            label: 'The ledger',
            value: balanced ? 'In balance' : 'Out of balance',
            tone: balanced ? 'positive' : 'negative',
            detail: balanced ? 'Debits equal credits' : <>Out by <Money minor={num(data.differenceMinor)} /></>,
          },
          { label: 'Nominal codes', value: number(rows.length), detail: 'With a balance or a movement' },
        ],
        sections: [
          {
            key: 'trial-balance',
            title: 'Balances by nominal code',
            columns: [
              {
                key: 'accountCode',
                header: 'Code',
                kind: 'code',
                width: '4.75rem',
                sortable: true,
                linkTo: (row) =>
                  ledgerEnquiry([['accountId', text(row.accountId)], ['periodId', ctx.params.periodId]]),
              },
              { key: 'accountName', header: 'Account', sortable: true },
              { key: 'accountType', header: 'Type', kind: 'label', width: '6.5rem' },
              { key: 'openingDebitMinor', header: 'Opening Dr', kind: 'money', zeroBlank: true },
              { key: 'openingCreditMinor', header: 'Opening Cr', kind: 'money', zeroBlank: true },
              { key: 'periodDebitMinor', header: 'Movement Dr', kind: 'money', zeroBlank: true },
              { key: 'periodCreditMinor', header: 'Movement Cr', kind: 'money', zeroBlank: true },
              { key: 'closingDebitMinor', header: 'Closing Dr', kind: 'money', zeroBlank: true, sortable: true },
              { key: 'closingCreditMinor', header: 'Closing Cr', kind: 'money', zeroBlank: true, sortable: true },
            ],
            rows,
            searchable: true,
            total: {
              label: 'Total',
              cells: {
                closingDebitMinor: <Money minor={num(data.totalDebitMinor)} bold />,
                closingCreditMinor: <Money minor={num(data.totalCreditMinor)} bold />,
              },
            },
          },
        ],
      };
    },
  },

  INCOME_EXPENDITURE: {
    params: 'period',
    run: async (p) => {
      /*
       * The engine only compares against budget when it is told which budget to
       * compare against, and there is no endpoint that simply names the control
       * version — budget versus actual returns it as a by-product. Without this
       * the budget column comes back as zero and the variance column reads as
       * the whole of income, which is the sort of figure that stops a meeting.
       * The lookup is best-effort: if it fails the account still reports, just
       * without its budget comparison.
       */
      const control = await api
        .get<{ budgetVersionId?: string | null }>(`/reports/budget-vs-actual${queryString([['periodId', p.periodId]])}`)
        .catch(() => null);
      return api.get<Payload>(
        `/reports/income-expenditure${queryString([
          ['periodId', p.periodId],
          ['budgetVersionId', control?.budgetVersionId ?? null],
        ])}`,
      );
    },
    build: (data) => ({
      stats: [
        { label: 'Income, year to date', value: <Money minor={num(data.totalIncomeYtdMinor)} /> },
        { label: 'Expenditure, year to date', value: <Money minor={num(data.totalExpenditureYtdMinor)} /> },
        {
          label: 'Surplus, year to date',
          value: <Money minor={num(data.surplusYtdMinor)} />,
          tone: (num(data.surplusYtdMinor) ?? 0) < 0 ? 'negative' : 'positive',
          detail: (
            <>
              Budget to date <Money minor={num(data.surplusBudgetYtdMinor)} />
            </>
          ),
        },
        { label: 'Surplus, this period', value: <Money minor={num(data.surplusPeriodMinor)} /> },
      ],
      sections: [incomeExpenditureSection(data, 'ie', 'Income and expenditure')],
    }),
  },

  BALANCE_SHEET: {
    params: 'period',
    run: (p) => api.get<Payload>(`/reports/balance-sheet${queryString([['periodId', p.periodId]])}`),
    build: (data) => {
      const balanced = data.isBalanced === true;
      const sections = rowsOf(data, 'sections').map((section, index) => {
        const title = text(section.title) ?? 'Balances';
        return {
          key: `bs-${index}`,
          title,
          columns: [
            { key: 'accountCode', header: 'Code', kind: 'code' as const, width: '4.75rem' },
            {
              key: 'accountName',
              header: 'Account',
              linkTo: (row: Row) => {
                const drill = objectOf(row, 'drillTo');
                const accountId = text(drill.accountId);
                return accountId ? ledgerEnquiry([['accountId', accountId], ['periodId', text(drill.periodId)]]) : null;
              },
            },
            { key: 'balanceMinor', header: 'At this period end', kind: 'money' as const },
            { key: 'priorBalanceMinor', header: 'At the start of the year', kind: 'money' as const },
          ],
          rows: rowsOf(section, 'lines'),
          total: {
            label: `Total ${lower(title)}`,
            cells: {
              balanceMinor: <Money minor={num(section.totalMinor)} bold />,
              priorBalanceMinor: <Money minor={num(section.priorTotalMinor)} bold />,
            },
          },
        };
      });

      return {
        stats: [
          { label: 'Total assets', value: <Money minor={num(data.totalAssetsMinor)} /> },
          { label: 'Total liabilities', value: <Money minor={num(data.totalLiabilitiesMinor)} /> },
          { label: 'Net assets', value: <Money minor={num(data.netAssetsMinor)} /> },
          {
            label: 'Reserves',
            value: <Money minor={num(data.totalReservesMinor)} />,
            detail: balanced ? 'Balances to net assets' : 'Does not balance to net assets',
          },
        ],
        notes: balanced ? null : (
          <Alert tone="error" title="The balance sheet does not balance">
            <p>
              Net assets and reserves differ by <Money minor={num(data.differenceMinor)} />. The figures below are
              shown as posted; the difference must be investigated before the statement is issued.
            </p>
          </Alert>
        ),
        sections,
      };
    },
  },

  GENERAL_LEDGER: {
    params: 'scope',
    run: (p) =>
      api.get<Payload>(
        `/reports/transactions${queryString([
          ['periodId', p.scope === 'period' ? p.periodId : null],
          ['financialYearId', p.scope === 'year' ? p.financialYearId : null],
          ['costCentreId', p.costCentreId],
          ['accountId', p.accountId],
          ['projectId', p.projectId],
        ])}`,
      ),
    build: (data, ctx) => {
      const rows: Row[] = rowsOf(data, 'transactions').map((row) => ({
        ...row,
        narrative: text(row.lineDescription) ?? text(row.description),
        costCentre: [text(row.costCentreCode), text(row.costCentreName)].filter(Boolean).join(' — '),
        account: [text(row.accountCode), text(row.accountName)].filter(Boolean).join(' — '),
      }));
      const totalCount = num(data.totalCount) ?? rows.length;
      const filtered = Boolean(ctx.params.costCentreId ?? ctx.params.accountId ?? ctx.params.projectId);
      const first = rows[0];

      return {
        parameters:
          ctx.params.scope === 'period'
            ? ctx.periodName
            : ctx.yearName
              ? `Year to date, ${ctx.yearName}`
              : null,
        stats: [
          { label: 'Postings shown', value: number(rows.length), detail: `of ${number(totalCount)} matching` },
          { label: 'Total debits', value: <Money minor={num(data.totalDebitMinor)} /> },
          { label: 'Total credits', value: <Money minor={num(data.totalCreditMinor)} /> },
        ],
        notes: (
          <>
            {filtered ? (
              <Alert tone="info" title="This enquiry is filtered">
                <p>
                  Showing postings for{' '}
                  {first ? (
                    <>
                      {ctx.params.costCentreId ? <strong>{text(first.costCentreName) ?? lower(ctx.t.costCentre)}</strong> : null}
                      {ctx.params.costCentreId && ctx.params.accountId ? ' · ' : null}
                      {ctx.params.accountId ? <strong>{text(first.accountName) ?? 'the selected account'}</strong> : null}
                    </>
                  ) : (
                    <>the {lower(ctx.t.costCentre)} and account you came from</>
                  )}
                  .{' '}
                  <Link to="/reports/GENERAL_LEDGER">Show every posting instead</Link>.
                </p>
              </Alert>
            ) : null}
            {totalCount > rows.length ? (
              <Alert tone="info" title="Only the most recent postings are shown">
                <p>
                  This enquiry returns the {number(rows.length)} most recent of {number(totalCount)} matching postings.
                  Choose a single period above to see a complete list.
                </p>
              </Alert>
            ) : null}
          </>
        ),
        sections: [
          {
            key: 'transactions',
            title: 'Postings',
            columns: [
              { key: 'effectiveDate', header: 'Date', kind: 'date', width: '6.25rem', sortable: true },
              {
                key: 'journalNo',
                header: 'Journal',
                kind: 'code',
                width: '10.5rem',
                sortable: true,
                linkTo: (row) => {
                  const journalId = text(row.journalId);
                  return journalId ? `/ledger/journals/${journalId}` : null;
                },
              },
              { key: 'documentType', header: 'Type', kind: 'label', width: '4.5rem' },
              { key: 'narrative', header: 'Narrative' },
              { key: 'costCentre', header: ctx.t.costCentre },
              { key: 'account', header: 'Account' },
              { key: 'debitMinor', header: 'Debit', kind: 'money', zeroBlank: true, sortable: true },
              { key: 'creditMinor', header: 'Credit', kind: 'money', zeroBlank: true, sortable: true },
            ],
            rows,
            searchable: true,
            pageSize: 25,
            empty: 'No postings match this enquiry.',
            total: {
              label: 'Total for this enquiry',
              cells: {
                debitMinor: <Money minor={num(data.totalDebitMinor)} bold />,
                creditMinor: <Money minor={num(data.totalCreditMinor)} bold />,
              },
            },
          },
        ],
      };
    },
  },

  MANAGEMENT_ACCOUNTS: {
    params: 'period',
    run: (p) => api.get<Payload>(`/reports/management-accounts${queryString([['periodId', p.periodId]])}`),
    build: (data, ctx) => {
      const ie = objectOf(data, 'incomeExpenditure');
      const bs = objectOf(data, 'balanceSheet');
      const departments = objectOf(data, 'departmentSummary');
      const commitments = objectOf(data, 'commitments');
      const budgetVersion = objectOf(data, 'budgetVersion');

      return {
        stats: [
          {
            label: 'Surplus, year to date',
            value: <Money minor={num(ie.surplusYtdMinor)} />,
            tone: (num(ie.surplusYtdMinor) ?? 0) < 0 ? 'negative' : 'positive',
            detail: (
              <>
                Budget to date <Money minor={num(ie.surplusBudgetYtdMinor)} />
              </>
            ),
          },
          { label: 'Net assets', value: <Money minor={num(bs.netAssetsMinor)} /> },
          { label: 'Cash at bank', value: <Money minor={num(data.cashPositionMinor)} />, to: '/cash', drillLabel: 'Open the cash book' },
          {
            label: 'Open commitments',
            value: <Money minor={(num(commitments.hardMinor) ?? 0) + (num(commitments.softMinor) ?? 0)} />,
            detail: (
              <>
                Ordered <Money minor={num(commitments.hardMinor)} /> · requested{' '}
                <Money minor={num(commitments.softMinor)} />
              </>
            ),
            to: '/reports/COMMITMENTS',
            drillLabel: 'See the commitments',
          },
        ],
        notes: text(budgetVersion.name) ? (
          <p className="smaller muted">
            Budget comparisons use {text(budgetVersion.name)}
            {text(budgetVersion.code) ? ` (${text(budgetVersion.code)})` : ''}, the control version for this year.
          </p>
        ) : null,
        sections: [
          incomeExpenditureSection(ie, 'ma-ie', 'Income and expenditure'),
          {
            key: 'ma-departments',
            title: `Budget and actual by ${lower(ctx.t.costCentre)}`,
            columns: [
              { key: 'costCentreCode', header: 'Code', kind: 'code', width: '4.75rem' },
              {
                key: 'costCentreName',
                header: ctx.t.costCentre,
                indent: true,
                linkTo: (row) => ledgerEnquiry([['costCentreId', text(row.costCentreId)]]),
              },
              ...BUDGET_COLUMNS(ctx.t),
            ],
            rows: rowsOf(departments, 'rows'),
            searchable: true,
            total: { label: 'Total', cells: budgetTotalCells(objectOf(departments, 'totals')) },
          },
          {
            key: 'ma-balance-sheet',
            title: 'Balance sheet summary',
            columns: [
              { key: 'title', header: 'Section' },
              { key: 'totalMinor', header: 'At this period end', kind: 'money' },
              { key: 'priorTotalMinor', header: 'At the start of the year', kind: 'money' },
            ],
            rows: rowsOf(bs, 'sections'),
            total: {
              label: 'Net assets',
              cells: { totalMinor: <Money minor={num(bs.netAssetsMinor)} bold /> },
            },
          },
        ],
      };
    },
  },

  /* ---------------- Budgeting ---------------- */

  BUDGET_VS_ACTUAL: {
    params: 'year',
    run: (p) => api.get<Payload>(`/reports/budget-vs-actual${queryString([['financialYearId', p.financialYearId]])}`),
    build: (data, ctx) => {
      const totals = objectOf(data, 'totals');
      return {
        stats: [
          { label: 'Annual budget', value: <Money minor={num(totals.annualBudgetMinor)} /> },
          {
            label: 'Actual to date',
            value: <Money minor={num(totals.actualToDateMinor)} />,
            to: ledgerEnquiry([['financialYearId', ctx.params.financialYearId]]),
            drillLabel: 'See the postings',
          },
          {
            label: `Committed on ${lower(ctx.t.purchaseOrderPlural)}`,
            value: <Money minor={num(totals.commitmentMinor)} />,
            to: '/reports/COMMITMENTS',
            drillLabel: 'See the commitments',
          },
          {
            label: 'Available',
            value: <Money minor={num(totals.availableMinor)} />,
            detail: `${percent(num(totals.utilisationPercent))} of the annual budget used`,
          },
        ],
        sections: [
          {
            key: 'budget',
            title: `Budget and actual by ${lower(ctx.t.costCentre)}`,
            columns: [
              { key: 'costCentreCode', header: 'Code', kind: 'code', width: '4.75rem', sortable: true },
              {
                key: 'costCentreName',
                header: ctx.t.costCentre,
                indent: true,
                sortable: true,
                linkTo: (row) => ledgerEnquiry([['costCentreId', text(row.costCentreId)], ['financialYearId', ctx.params.financialYearId]]),
              },
              ...BUDGET_COLUMNS(ctx.t),
            ],
            rows: rowsOf(data, 'rows'),
            searchable: true,
            total: { label: 'Total', cells: budgetTotalCells(totals) },
          },
        ],
      };
    },
  },

  DEPARTMENTAL_STATEMENT: {
    params: 'periodAndCostCentre',
    run: (p) =>
      api.get<Payload>(
        `/reports/departmental-statement${queryString([
          ['periodId', p.periodId],
          ['costCentreId', p.costCentreId],
        ])}`,
      ),
    build: (data, ctx) => {
      const detail = objectOf(data, 'detail');
      const totals = objectOf(detail, 'totals');
      const costCentre = objectOf(data, 'costCentre');
      const holder = text(costCentre.budget_holder_name);
      return {
        stats: [
          { label: 'Annual budget', value: <Money minor={num(totals.annualBudgetMinor)} /> },
          {
            label: 'Actual to date',
            value: <Money minor={num(totals.actualToDateMinor)} />,
            to: ledgerEnquiry([['costCentreId', ctx.params.costCentreId]]),
            drillLabel: 'See the postings',
          },
          { label: `Committed on ${lower(ctx.t.purchaseOrderPlural)}`, value: <Money minor={num(totals.commitmentMinor)} /> },
          {
            label: 'Available',
            value: <Money minor={num(totals.availableMinor)} />,
            detail: `${percent(num(totals.utilisationPercent))} of the annual budget used`,
          },
        ],
        notes: holder ? (
          <p className="smaller muted">
            {ctx.t.budgetHolder}: {holder}.
          </p>
        ) : null,
        sections: [
          {
            key: 'dept-detail',
            title: 'Budget and actual by account',
            columns: [
              { key: 'accountCode', header: 'Code', kind: 'code', width: '4.75rem', sortable: true },
              {
                key: 'accountName',
                header: 'Account',
                sortable: true,
                linkTo: (row) =>
                  ledgerEnquiry([
                    ['costCentreId', ctx.params.costCentreId],
                    ['accountId', text(row.accountId)],
                    ['periodId', ctx.params.periodId],
                  ]),
              },
              ...BUDGET_COLUMNS(ctx.t),
            ],
            rows: rowsOf(detail, 'rows'),
            empty: 'No budget lines are held for this area.',
            total: { label: 'Total', cells: budgetTotalCells(totals) },
          },
          incomeExpenditureSection(objectOf(data, 'incomeExpenditure'), 'dept-ie', 'Income and expenditure for this area'),
        ],
      };
    },
  },

  COMMITMENTS: {
    params: 'none',
    run: () => api.get<Payload>('/procurement/commitments'),
    build: (data, ctx) => {
      const totals = objectOf(data, 'totals');
      const hard = num(totals.hardMinor) ?? 0;
      const soft = num(totals.softMinor) ?? 0;
      const rows: Row[] = rowsOf(data, 'commitments').map((row) => ({
        ...row,
        costCentre: [text(row.cost_centre_code), text(row.cost_centre_name)].filter(Boolean).join(' — '),
        account: [text(row.account_code), text(row.account_name)].filter(Boolean).join(' — '),
      }));
      return {
        parameters: `Open commitments as at ${date(todayIso())}`,
        stats: [
          {
            label: `Ordered — on ${lower(ctx.t.purchaseOrderPlural)}`,
            value: <Money minor={hard} />,
            to: '/orders',
            drillLabel: `See the ${lower(ctx.t.purchaseOrderPlural)}`,
          },
          {
            label: `Requested — on ${lower(ctx.t.requisitionPlural)}`,
            value: <Money minor={soft} />,
            to: '/requisitions',
            drillLabel: `See the ${lower(ctx.t.requisitionPlural)}`,
          },
          { label: 'Total committed', value: <Money minor={hard + soft} />, detail: 'Charged against budget but not yet invoiced' },
        ],
        sections: [
          {
            key: 'commitments',
            title: 'Open commitments',
            columns: [
              {
                key: 'source_reference',
                header: 'Raised on',
                kind: 'code',
                width: '11rem',
                sortable: true,
                linkTo: (row) => {
                  const id = text(row.source_id);
                  const table = text(row.source_table);
                  if (!id) return null;
                  if (table === 'purchase_order') return `/orders/${id}`;
                  if (table === 'purchase_requisition') return `/requisitions/${id}`;
                  return null;
                },
              },
              { key: 'commitment_type', header: 'Type', kind: 'label', width: '5rem' },
              { key: 'costCentre', header: ctx.t.costCentre },
              { key: 'account', header: 'Account' },
              { key: 'original_amount_minor', header: 'Original', kind: 'money', sortable: true },
              { key: 'relieved_amount_minor', header: 'Relieved', kind: 'money', zeroBlank: true },
              { key: 'outstanding_amount_minor', header: 'Outstanding', kind: 'money', sortable: true },
              { key: 'status', header: 'Status', kind: 'status', width: '8rem' },
            ],
            rows,
            searchable: true,
            empty: 'No commitments are open.',
            total: { label: 'Total outstanding', cells: { outstanding_amount_minor: <Money minor={hard + soft} bold /> } },
          },
        ],
      };
    },
  },

  /* ---------------- Purchase ledger ---------------- */

  AGED_CREDITORS: {
    params: 'none',
    run: () => api.get<Payload>('/payables/aged-creditors'),
    build: (data, ctx) => {
      const rows: Row[] = rowsOf(data, 'suppliers').map((row) => ({ ...row, ...bucketsFromArray(row.buckets) }));
      const grand = num(data.grandTotalMinor);
      const over90 = bucketsFromArray(data.totals).age90plus ?? 0;
      return {
        parameters: `As at ${date(text(data.asAt))}`,
        stats: [
          {
            label: `Owed to ${lower(ctx.t.supplierPlural)}`,
            value: <Money minor={grand} />,
            to: '/invoices',
            drillLabel: 'See the invoices',
          },
          { label: `${ctx.t.supplierPlural} with a balance`, value: number(rows.length) },
          {
            label: 'Over 90 days',
            value: <Money minor={over90} />,
            tone: over90 > 0 ? 'negative' : undefined,
            detail: over90 > 0 ? 'Overdue by more than three months' : 'Nothing has aged beyond 90 days',
          },
        ],
        sections: [
          {
            key: 'aged-creditors',
            title: `Balances by ageing bucket`,
            subtitle: 'Aged from the due date on each invoice.',
            columns: [
              { key: 'supplierCode', header: 'Code', kind: 'code', width: '6rem', sortable: true },
              {
                key: 'supplierName',
                header: ctx.t.supplier,
                sortable: true,
                linkTo: (row) => {
                  const id = text(row.supplierId);
                  return id ? `/suppliers/${id}` : null;
                },
              },
              ...ageingColumns(),
              { key: 'totalMinor', header: 'Total', kind: 'money', sortable: true },
            ],
            rows,
            searchable: true,
            empty: `Nothing is owed to any ${lower(ctx.t.supplier)}.`,
            total: { label: 'Total owed', cells: ageingTotalCells(data.totals, grand) },
          },
        ],
      };
    },
  },

  SUPPLIER_SPEND: {
    params: 'year',
    run: (p) => api.get<Payload>(`/reports/supplier-spend${queryString([['financialYearId', p.financialYearId]])}`),
    build: (data, ctx) => {
      const rows = rowsOf(data, 'suppliers');
      return {
        parameters: ctx.yearName ? `Financial year ${ctx.yearName}` : null,
        stats: [
          { label: 'Net spend', value: <Money minor={num(data.totalNetSpendMinor)} /> },
          {
            label: `${ctx.t.supplierPlural} invoiced`,
            value: number(rows.length),
            to: '/suppliers',
            drillLabel: `See the ${lower(ctx.t.supplierPlural)}`,
          },
        ],
        sections: [
          {
            key: 'supplier-spend',
            title: `Net spend by ${lower(ctx.t.supplier)}`,
            columns: [
              {
                key: 'supplier_code',
                header: 'Code',
                kind: 'code',
                width: '6rem',
                sortable: true,
                linkTo: (row) => {
                  const id = text(row.supplier_id);
                  return id ? `/suppliers/${id}` : null;
                },
              },
              {
                key: 'supplier_name',
                header: ctx.t.supplier,
                sortable: true,
                linkTo: (row) => {
                  const id = text(row.supplier_id);
                  return id ? `/suppliers/${id}` : null;
                },
              },
              { key: 'supplier_type', header: 'Type', kind: 'label', width: '6.5rem' },
              { key: 'categories', header: 'Categories', kind: 'list' },
              { key: 'invoice_count', header: 'Invoices', kind: 'number', width: '5.5rem' },
              { key: 'net_spend_minor', header: 'Net spend', kind: 'money', sortable: true },
              { key: 'outstanding_minor', header: 'Outstanding', kind: 'money', zeroBlank: true, sortable: true },
              { key: 'contract_reference', header: 'Contract', kind: 'code', width: '8rem' },
            ],
            rows,
            searchable: true,
            empty: `No ${lower(ctx.t.supplier)} has been invoiced in this year.`,
            total: { label: 'Total net spend', cells: { net_spend_minor: <Money minor={num(data.totalNetSpendMinor)} bold /> } },
          },
        ],
      };
    },
  },

  CASH_REQUIREMENT: {
    params: 'none',
    run: () => api.get<Payload>('/payables/cash-requirement'),
    build: (data) => {
      const buckets: Row[] = rowsOf(data, 'buckets').map((b) => {
        const label = text(b.label) ?? '';
        return { ...b, label: CASH_REQUIREMENT_LABELS[label] ?? statusLabel(label) };
      });
      const total = buckets.reduce((sum, b) => sum + (num(b.amountMinor) ?? 0), 0);
      const invoices = buckets.reduce((sum, b) => sum + (num(b.invoiceCount) ?? 0), 0);
      const overdue =
        num(rowsOf(data, 'buckets').find((b) => text(b.label) === 'overdue')?.amountMinor) ?? 0;
      return {
        parameters: `Invoices falling due from ${date(todayIso())}`,
        stats: [
          {
            label: 'Falling due',
            value: <Money minor={total} />,
            detail: `${number(invoices)} invoices in the next 60 days`,
            to: '/payments',
            drillLabel: 'Open the payment runs',
          },
          {
            label: 'Already overdue',
            value: <Money minor={overdue} />,
            tone: overdue > 0 ? 'negative' : undefined,
            detail: overdue > 0 ? 'Past the agreed payment terms' : 'Nothing is past its due date',
          },
        ],
        sections: [
          {
            key: 'cash-requirement',
            title: 'Cash needed to settle the purchase ledger',
            columns: [
              { key: 'label', header: 'Falling due', width: '12rem' },
              { key: 'invoiceCount', header: 'Invoices', kind: 'number', width: '6rem' },
              { key: 'amountMinor', header: 'Amount', kind: 'money', zeroBlank: true },
            ],
            rows: buckets,
            empty: 'No invoices are outstanding.',
            total: {
              label: 'Total',
              cells: {
                invoiceCount: <span className="strong">{number(invoices)}</span>,
                amountMinor: <Money minor={total} bold />,
              },
            },
          },
        ],
      };
    },
  },

  /* ---------------- Sales ledger ---------------- */

  AGED_DEBTORS: {
    params: 'none',
    run: () => api.get<Payload>('/receivables/aged-debtors'),
    build: (data, ctx) => {
      const rows: Row[] = rowsOf(data, 'customers').map((row) => ({ ...row, ...bucketsFromObject(row.buckets) }));
      const grand = num(data.grandTotalMinor);
      const over90 = bucketsFromArray(data.totals).age90plus ?? 0;
      return {
        parameters: `As at ${date(text(data.asAt))}`,
        stats: [
          {
            label: `Owed by ${lower(ctx.t.customerPlural)}`,
            value: <Money minor={grand} />,
            to: '/debtors',
            drillLabel: 'Open the sales ledger',
          },
          { label: `${ctx.t.customerPlural} with a balance`, value: number(rows.length) },
          {
            label: 'Over 90 days',
            value: <Money minor={over90} />,
            tone: over90 > 0 ? 'negative' : undefined,
            detail: over90 > 0 ? 'Overdue by more than three months' : 'Nothing has aged beyond 90 days',
          },
        ],
        sections: [
          {
            key: 'aged-debtors',
            title: 'Balances by ageing bucket',
            subtitle: 'Aged from the due date on each invoice.',
            columns: [
              { key: 'code', header: 'Code', kind: 'code', width: '6rem', sortable: true },
              { key: 'name', header: ctx.t.customer, sortable: true },
              { key: 'type', header: 'Category', kind: 'label', width: '9rem' },
              ...ageingColumns(),
              { key: 'totalMinor', header: 'Total', kind: 'money', sortable: true },
            ],
            rows,
            searchable: true,
            empty: `Nothing is owed by any ${lower(ctx.t.customer)}.`,
            total: { label: 'Total owed', cells: ageingTotalCells(data.totals, grand) },
          },
        ],
      };
    },
  },

  /* ---------------- Cash book ---------------- */

  CASH_POSITION: {
    params: 'none',
    run: () => api.get<Payload>('/reports/cash-position'),
    build: (data) => {
      const accounts = rowsOf(data, 'accounts');
      const unreconciled = accounts.reduce((sum, a) => sum + (num(a.unreconciled_count) ?? 0), 0);
      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          {
            label: 'Cash at bank, per the ledger',
            value: <Money minor={num(data.totalLedgerMinor)} />,
            to: '/cash',
            drillLabel: 'Open the cash book',
          },
          { label: 'Per the latest statements', value: <Money minor={num(data.totalStatementMinor)} /> },
          {
            label: 'Unreconciled items',
            value: number(unreconciled),
            detail: unreconciled > 0 ? 'Statement lines not yet matched' : 'Every statement line is matched',
            to: '/cash',
            drillLabel: 'Reconcile them',
          },
        ],
        sections: [
          {
            key: 'cash-position',
            title: 'Bank accounts',
            columns: [
              { key: 'code', header: 'Code', kind: 'code', width: '6rem' },
              { key: 'name', header: 'Account' },
              { key: 'bank_name', header: 'Bank' },
              { key: 'account_type', header: 'Type', kind: 'label', width: '7rem' },
              { key: 'ledger_balance_minor', header: 'Per the ledger', kind: 'money', sortable: true },
              { key: 'statement_balance_minor', header: 'Per the statement', kind: 'money' },
              { key: 'last_statement_date', header: 'Last statement', kind: 'date', width: '7.5rem' },
              { key: 'unreconciled_count', header: 'Unreconciled', kind: 'number', width: '7rem' },
            ],
            rows: accounts,
            empty: 'No bank accounts are held.',
            total: {
              label: 'Total',
              cells: {
                ledger_balance_minor: <Money minor={num(data.totalLedgerMinor)} bold />,
                statement_balance_minor: <Money minor={num(data.totalStatementMinor)} bold />,
                unreconciled_count: <span className="strong">{number(unreconciled)}</span>,
              },
            },
          },
        ],
      };
    },
  },

  /* ---------------- Fixed assets ---------------- */

  FIXED_ASSET_REGISTER: {
    params: 'none',
    run: () => api.get<Payload>('/reports/fixed-assets'),
    build: (data) => {
      const totals = objectOf(data, 'totals');
      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          { label: 'Cost', value: <Money minor={num(totals.costMinor)} />, to: '/assets', drillLabel: 'Open the register' },
          { label: 'Accumulated depreciation', value: <Money minor={num(totals.depreciationMinor)} /> },
          { label: 'Net book value', value: <Money minor={num(totals.nbvMinor)} /> },
          { label: 'Assets held', value: number(num(totals.count)) },
        ],
        sections: [
          {
            key: 'assets',
            title: 'Assets',
            columns: [
              { key: 'asset_no', header: 'Asset', kind: 'code', width: '10.5rem', sortable: true },
              { key: 'name', header: 'Description', sortable: true },
              { key: 'category_name', header: 'Category', sortable: true },
              { key: 'location_name', header: 'Location' },
              { key: 'custodian_name', header: 'Custodian' },
              { key: 'current_cost_minor', header: 'Cost', kind: 'money', sortable: true },
              { key: 'accumulated_depreciation_minor', header: 'Depreciation', kind: 'money', zeroBlank: true },
              { key: 'net_book_value_minor', header: 'Net book value', kind: 'money', sortable: true },
            ],
            rows: rowsOf(data, 'assets'),
            searchable: true,
            pageSize: 25,
            empty: 'No assets are held.',
            total: {
              label: 'Total for the whole register',
              cells: {
                current_cost_minor: <Money minor={num(totals.costMinor)} bold />,
                accumulated_depreciation_minor: <Money minor={num(totals.depreciationMinor)} bold />,
                net_book_value_minor: <Money minor={num(totals.nbvMinor)} bold />,
              },
            },
          },
          {
            key: 'assets-by-category',
            title: 'Summary by category',
            columns: [
              { key: 'code', header: 'Code', kind: 'code', width: '6rem' },
              { key: 'name', header: 'Category' },
              { key: 'count', header: 'Assets', kind: 'number', width: '5.5rem' },
              { key: 'costMinor', header: 'Cost', kind: 'money' },
              { key: 'depreciationMinor', header: 'Depreciation', kind: 'money', zeroBlank: true },
              { key: 'nbvMinor', header: 'Net book value', kind: 'money' },
            ],
            rows: rowsOf(data, 'byCategory'),
            empty: 'No categories are in use.',
            total: {
              label: 'Total',
              cells: {
                count: <span className="strong">{number(num(totals.count))}</span>,
                costMinor: <Money minor={num(totals.costMinor)} bold />,
                depreciationMinor: <Money minor={num(totals.depreciationMinor)} bold />,
                nbvMinor: <Money minor={num(totals.nbvMinor)} bold />,
              },
            },
          },
        ],
      };
    },
  },

  GRANT_REGISTER: {
    params: 'none',
    run: () => api.get<Payload>('/assets/grant-register'),
    build: (data) => {
      const totals = objectOf(data, 'totals');
      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          { label: 'Grants awarded', value: <Money minor={num(totals.awardedMinor)} />, to: '/assets', drillLabel: 'Open the register' },
          { label: 'Released to income', value: <Money minor={num(totals.releasedMinor)} /> },
          { label: 'Still deferred', value: <Money minor={num(totals.remainingMinor)} /> },
        ],
        sections: [
          {
            key: 'grants',
            title: 'Grant-funded assets',
            subtitle: 'Deferred capital grants are released to income over the life of the asset they funded.',
            columns: [
              { key: 'asset_no', header: 'Asset', kind: 'code', width: '10.5rem', sortable: true },
              { key: 'name', header: 'Description', sortable: true },
              { key: 'grant_funder', header: 'Funder' },
              { key: 'grant_reference', header: 'Reference', kind: 'code', width: '8rem' },
              { key: 'acquisition_date', header: 'Acquired', kind: 'date', width: '7rem' },
              { key: 'grant_amount_minor', header: 'Grant awarded', kind: 'money', sortable: true },
              { key: 'grant_released_minor', header: 'Released', kind: 'money', zeroBlank: true },
              { key: 'grant_remaining_minor', header: 'Still deferred', kind: 'money' },
              { key: 'net_book_value_minor', header: 'Asset net book value', kind: 'money' },
            ],
            rows: rowsOf(data, 'assets'),
            empty: 'No asset in the register is grant funded.',
            total: {
              label: 'Total',
              cells: {
                grant_amount_minor: <Money minor={num(totals.awardedMinor)} bold />,
                grant_released_minor: <Money minor={num(totals.releasedMinor)} bold />,
                grant_remaining_minor: <Money minor={num(totals.remainingMinor)} bold />,
              },
            },
          },
        ],
      };
    },
  },

  /* ---------------- Projects ---------------- */

  PROJECT_ACCOUNTING: {
    params: 'none',
    run: () => api.get<Payload>('/reports/project-accounting'),
    build: (data, ctx) => {
      const projects = rowsOf(data, 'projects');
      // Summing integers the server already computed, only to show the reader a
      // subtotal of the rows in front of them.
      const sum = (key: string) => projects.reduce((total, p) => total + (num(p[key]) ?? 0), 0);
      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          {
            label: 'Awarded',
            value: <Money minor={sum('award_amount_minor')} />,
            to: '/projects',
            drillLabel: `Open the ${lower(ctx.t.projectPlural)}`,
          },
          { label: 'Expenditure to date', value: <Money minor={sum('expenditure_minor')} /> },
          { label: 'Income recognised', value: <Money minor={sum('income_minor')} /> },
          { label: `${ctx.t.projectPlural} reported`, value: number(projects.length) },
        ],
        sections: [
          {
            key: 'projects',
            title: `${ctx.t.projectPlural} and grants`,
            columns: [
              { key: 'code', header: 'Code', kind: 'code', width: '7rem', sortable: true },
              { key: 'name', header: ctx.t.project, sortable: true },
              { key: 'funder_name', header: 'Funder' },
              { key: 'status', header: 'Status', kind: 'status', width: '7rem' },
              { key: 'award_amount_minor', header: 'Award', kind: 'money', sortable: true },
              { key: 'expenditure_minor', header: 'Expenditure', kind: 'money', sortable: true },
              { key: 'income_minor', header: 'Income', kind: 'money', zeroBlank: true },
              { key: 'commitment_minor', header: 'Committed', kind: 'money', zeroBlank: true },
              { key: 'available_minor', header: 'Available', kind: 'money' },
            ],
            rows: projects,
            searchable: true,
            empty: `No ${lower(ctx.t.project)} is open.`,
            total: {
              label: 'Total',
              cells: {
                award_amount_minor: <Money minor={sum('award_amount_minor')} bold />,
                expenditure_minor: <Money minor={sum('expenditure_minor')} bold />,
                income_minor: <Money minor={sum('income_minor')} blankIfZero bold />,
                commitment_minor: <Money minor={sum('commitment_minor')} blankIfZero bold />,
                available_minor: <Money minor={sum('available_minor')} bold />,
              },
            },
          },
        ],
      };
    },
  },

  /* ---------------- Audit and control ---------------- */

  SEGREGATION_OF_DUTIES: {
    params: 'none',
    run: () => api.get<Payload>('/operations/security/segregation'),
    build: (data) => {
      const findings: Row[] = rowsOf(data, 'findings').map((finding) => {
        const users = Array.isArray(finding.users) ? (finding.users as Row[]) : [];
        const severity = text(finding.severity);
        return {
          ...finding,
          // 'block' and 'warn' are the rule table's own words; neither is what a
          // security reviewer would call the finding in front of them.
          severityLabel: severity === 'block' ? 'Prohibited' : severity === 'warn' ? 'Review' : statusLabel(severity),
          usersText: users.map((u) => text(u.full_name) ?? text(u.username) ?? '').filter(Boolean).join(', '),
          permissionsText: Array.isArray(finding.permissions)
            ? (finding.permissions as unknown[]).map((p) => text(p) ?? '').filter(Boolean).join('  +  ')
            : '',
        };
      });
      const controls = Array.isArray(data.runtimeControls) ? (data.runtimeControls as unknown[]) : [];
      const ruleCount = rowsOf(data, 'rules').length;
      const clean = num(data.cleanRuleCount) ?? 0;

      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          {
            label: 'Conflicts found',
            value: number(findings.length),
            tone: findings.length > 0 ? 'negative' : 'positive',
            detail: `of ${number(ruleCount)} rules tested`,
          },
          { label: 'Rules with no conflict', value: number(clean) },
          { label: 'Controls enforced at run time', value: number(controls.length) },
        ],
        notes: (
          <>
            {text(data.summary) ? <p className="smaller muted">{text(data.summary)}</p> : null}
            {controls.length > 0 ? (
              <Card title="Controls the system enforces however permissions are held">
                <ul className="stack stack--sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {controls.map((control, index) => {
                    const label = text(control);
                    return label ? (
                      <li key={index}>
                        <Badge tone="ok">Enforced</Badge> {label}
                      </li>
                    ) : null;
                  })}
                </ul>
              </Card>
            ) : null}
          </>
        ),
        sections: [
          {
            key: 'segregation',
            title: 'Users holding a conflicting combination',
            columns: [
              {
                key: 'ruleName',
                header: 'Control rule',
                render: (row) => (
                  <>
                    <div className="strong">{text(row.ruleName) ?? '—'}</div>
                    <div className="smaller muted">{text(row.description)}</div>
                  </>
                ),
              },
              {
                key: 'severityLabel',
                header: 'Severity',
                width: '7.5rem',
                render: (row) => (
                  <Badge tone={statusTone(text(row.severity))}>{text(row.severityLabel) ?? '—'}</Badge>
                ),
              },
              {
                key: 'permissionsText',
                header: 'Permissions in conflict',
                width: '15rem',
                render: (row) => <span className="mono smaller">{text(row.permissionsText) ?? '—'}</span>,
              },
              { key: 'usersText', header: 'Held by' },
              { key: 'mitigation', header: 'What should happen' },
            ],
            rows: findings,
            empty: 'No user holds a conflicting combination of permissions.',
          },
        ],
      };
    },
  },

  BANK_DETAIL_REGISTER: {
    params: 'none',
    run: () => api.get<Payload>('/operations/security/bank-register'),
    build: (data, ctx) => {
      const accounts = rowsOf(data, 'accounts');
      const warnings = num(data.warningCount) ?? 0;
      return {
        parameters: `As at ${date(todayIso())}`,
        stats: [
          {
            label: 'Bank accounts on file',
            value: number(accounts.length),
            to: '/suppliers',
            drillLabel: `See the ${lower(ctx.t.supplierPlural)}`,
          },
          {
            label: 'Awaiting verification',
            value: number(warnings),
            tone: warnings > 0 ? 'negative' : 'positive',
            detail: warnings > 0 ? 'These accounts cannot be paid' : 'Every account has been independently verified',
          },
        ],
        notes:
          data.showingFullAccountNumbers === true ? (
            <Alert tone="warn" title="Full account numbers are shown">
              <p>
                Your role carries permission to see {lower(ctx.t.supplier)} bank details in full. Anyone without it
                sees the same register with the account numbers masked.
              </p>
            </Alert>
          ) : null,
        sections: [
          {
            key: 'bank-register',
            title: `${ctx.t.supplier} bank details`,
            columns: [
              { key: 'supplierCode', header: 'Code', kind: 'code', width: '6rem', sortable: true },
              { key: 'supplierName', header: ctx.t.supplier, sortable: true },
              { key: 'sortCode', header: 'Sort code', kind: 'code', width: '6.5rem' },
              { key: 'accountNumber', header: 'Account number', kind: 'code', width: '8rem' },
              { key: 'status', header: 'Status', kind: 'status', width: '9rem' },
              { key: 'proposedBy', header: 'Proposed by' },
              { key: 'verifiedBy', header: 'Verified by' },
              { key: 'controlWarning', header: 'Control note' },
            ],
            rows: accounts,
            searchable: true,
            empty: `No ${lower(ctx.t.supplier)} bank details are held.`,
          },
        ],
      };
    },
  },

  /*
   * The audit trail has a screen of its own with filtering, a full event
   * timeline and its own export. Offering a lesser copy of it here would be a
   * worse answer than taking the reader to the real one.
   */
  AUDIT_TRAIL: { openIn: '/audit' },
};

/* ------------------------------------------------------------------ *
 * The reporting centre index
 * ------------------------------------------------------------------ */

export function ReportsPage() {
  const { code } = useParams<{ code?: string }>();
  /*
   * Report codes are the catalogue's own upper-case constants, but links are
   * written by hand all over the product — the budget screen and the close
   * checklist both point at /reports/budget-vs-actual and
   * /reports/management-accounts. Accepting either spelling costs one line and
   * saves those links from landing on a refusal.
   */
  if (code) return <ReportRunner code={code.trim().toUpperCase().replace(/-/g, '_')} />;
  return <ReportCatalogue />;
}

function ReportCatalogue() {
  const catalogue = useAsync(() => api.get<{ reports: ReportDefinition[] }>('/reports/catalogue'), []);

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Every figure is computed from posted transactions when you run it."
      />
      <AsyncPanel state={catalogue}>
        {(data) => {
          /*
           * A report is only listed when this screen can actually produce it.
           * Offering a report and then refusing it is worse than not offering
           * it, and the catalogue is a database table that can run ahead of the
           * engines wired up here.
           */
          const runnable = data.reports.filter((r) => REPORTS[r.code] !== undefined);
          if (runnable.length === 0) {
            return (
              <EmptyState title="No reports are available to you">
                Your permissions do not include any of the reports in this catalogue. Your finance system
                administrator can grant access.
              </EmptyState>
            );
          }

          const byCategory = new Map<string, ReportDefinition[]>();
          for (const r of runnable) {
            const existing = byCategory.get(r.category);
            if (existing) existing.push(r);
            else byCategory.set(r.category, [r]);
          }

          return (
            <div className="grid grid--2">
              {[...byCategory.entries()].map(([category, reports]) => (
                <Card key={category} title={category}>
                  {/* The list carries the grouping for a screen reader; the discs
                      and the default indent are not wanted on a menu of links. */}
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {reports.map((r, index) => {
                      const entry = REPORTS[r.code];
                      const to = entry && isRedirect(entry) ? entry.openIn : `/reports/${r.code}`;
                      return (
                        <li key={r.code} style={index > 0 ? { borderTop: '1px solid var(--ink-200)' } : undefined}>
                          <Link
                            to={to}
                            style={{
                              display: 'block',
                              padding: '0.625rem 0.25rem',
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            <span className="strong" style={{ color: 'var(--brand-700)' }}>
                              {r.name}
                            </span>
                            <span className="smaller muted" style={{ display: 'block', marginTop: '0.125rem' }}>
                              {r.description}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              ))}
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Running one report
 * ------------------------------------------------------------------ */

interface PeriodOption {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isAdjustmentPeriod: boolean;
  financialYearId: string;
  financialYearCode: string;
}

interface YearOption {
  id: string;
  code: string;
  name: string;
}

interface CostCentreOption {
  id: string;
  code: string;
  name: string;
  depth: number;
  level_type: string;
}

interface Reference {
  periods: PeriodOption[];
  years: YearOption[];
  costCentres: CostCentreOption[];
  currentPeriodId: string | null;
  currentYearId: string | null;
}

const EMPTY_REFERENCE: Reference = {
  periods: [],
  years: [],
  costCentres: [],
  currentPeriodId: null,
  currentYearId: null,
};

function ReportRunner({ code }: { code: string }) {
  const entry = REPORTS[code];
  const catalogue = useAsync(() => api.get<{ reports: ReportDefinition[] }>('/reports/catalogue'), []);
  const definition = catalogue.data?.reports.find((r) => r.code === code) ?? null;
  // Readable before the catalogue answers, and never the shouty enum itself.
  const title = definition?.name ?? humanise(code.toLowerCase());

  const needsReference = entry !== undefined && !isRedirect(entry) && entry.params !== 'none';

  const reference = useAsync(async (): Promise<Reference> => {
    if (!needsReference) return EMPTY_REFERENCE;
    const wantsCostCentres = entry !== undefined && !isRedirect(entry) && entry.params === 'periodAndCostCentre';
    const [periods, years, costCentres] = await Promise.all([
      api.get<{ periods: PeriodOption[] }>('/reference/periods'),
      api.get<{ financialYears: YearOption[]; currentFinancialYearId: string }>('/reference/financial-years'),
      wantsCostCentres
        ? api.get<{ costCentres: CostCentreOption[] }>('/reference/cost-centres')
        : Promise.resolve({ costCentres: [] as CostCentreOption[] }),
    ]);
    const today = todayIso();
    const open = periods.periods.filter((p) => !p.isAdjustmentPeriod);
    const current =
      open.find((p) => p.startDate <= today && p.endDate >= today) ??
      [...open].reverse().find((p) => p.startDate <= today) ??
      open[0] ??
      null;
    return {
      periods: periods.periods,
      years: years.financialYears,
      costCentres: costCentres.costCentres,
      currentPeriodId: current?.id ?? null,
      currentYearId: years.currentFinancialYearId,
    };
  }, [code, needsReference]);

  if (!entry) {
    return (
      <>
        <PageHeader
          title={title}
          subtitle={definition?.description}
          actions={
            <Link className="btn" to="/reports">
              All reports
            </Link>
          }
        />
        <EmptyState title="This report cannot be produced yet">
          <p>
            The engine behind {title.toLowerCase()} is not available in this release, so the reporting centre does not
            offer it. Everything else in the catalogue runs normally.
          </p>
        </EmptyState>
      </>
    );
  }

  // Some catalogue entries are screens in their own right rather than tables.
  if (isRedirect(entry)) return <Navigate to={entry.openIn} replace />;

  return (
    <AsyncPanel state={reference}>
      {(ref) => (
        <ReportBody key={code} code={code} spec={entry} definition={definition} title={title} reference={ref} />
      )}
    </AsyncPanel>
  );
}

function ReportBody({
  code,
  spec,
  definition,
  title,
  reference,
}: {
  code: string;
  spec: ReportSpec;
  definition: ReportDefinition | null;
  title: string;
  reference: Reference;
}) {
  const t = useTerms();
  const { isDemonstration } = useTenant();
  const { user, activeEntity, can } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const inheritedCostCentre = searchParams.get('costCentreId');
  const inheritedAccount = searchParams.get('accountId');
  const inheritedProject = searchParams.get('projectId');
  const inheritedPeriod = searchParams.get('periodId');
  const inheritedYear = searchParams.get('financialYearId');
  const hasDrillFilter = Boolean(inheritedCostCentre ?? inheritedAccount ?? inheritedProject);

  const defaultCostCentre =
    reference.costCentres.find((c) => c.level_type === 'department') ??
    reference.costCentres.find((c) => c.depth > 0) ??
    reference.costCentres[0] ??
    null;

  const [periodId, setPeriodId] = useState<string | null>(inheritedPeriod ?? reference.currentPeriodId);
  const [yearId, setYearId] = useState<string | null>(inheritedYear ?? reference.currentYearId);
  const [costCentreId, setCostCentreId] = useState<string | null>(inheritedCostCentre ?? defaultCostCentre?.id ?? null);
  /*
   * A drill-down from a budget figure is a year-to-date question, so the ledger
   * enquiry opens on the whole year when it arrives carrying a filter but no
   * period of its own.
   */
  const [scope, setScope] = useState<'period' | 'year'>(
    inheritedPeriod ? 'period' : inheritedYear || hasDrillFilter ? 'year' : 'period',
  );

  const params: RunParams = {
    periodId,
    financialYearId: yearId,
    costCentreId: spec.params === 'periodAndCostCentre' ? costCentreId : inheritedCostCentre,
    accountId: inheritedAccount,
    projectId: inheritedProject,
    scope,
  };

  const requestKey = JSON.stringify([code, params]);
  const report = useAsync(() => spec.run(params), [requestKey]);

  // Stamped when the request is made, so the header names a real instant even
  // for the routers that send no header of their own.
  const runAt = useMemo(() => new Date().toISOString(), [requestKey]);

  const selectedPeriod = reference.periods.find((p) => p.id === periodId) ?? null;
  const selectedYear = reference.years.find((y) => y.id === yearId) ?? null;

  const ctx: BuildContext = {
    t,
    params,
    yearName: selectedYear?.name ?? null,
    periodName: selectedPeriod?.name ?? null,
  };

  // Provenance is only claimed for a report that actually produced figures. A
  // header saying "run by … at …" above a red failure box would be a lie.
  const produced = report.data !== null && report.error === null;
  const view = produced && report.data ? spec.build(report.data, ctx) : null;
  const meta = produced && report.data ? objectOf(report.data, 'meta') : {};

  const provenance = {
    reportName: text(meta.reportName) ?? definition?.name ?? title,
    entityName: text(meta.entityName) ?? activeEntity?.entityName ?? '',
    runBy: text(meta.runBy) ?? user?.fullName ?? '',
    runAtDisplay: text(meta.runAtDisplay) ?? dateTime(runAt),
    parameters: text(meta.parameters) ?? view?.parameters ?? null,
    dataNotice:
      text(meta.dataNotice) ??
      (isDemonstration ? 'Produced from a demonstration system containing synthetic data.' : null),
  };

  const exportCsv = useMutation((payload: Parameters<typeof api.exportCsv>[0]) => api.exportCsv(payload));

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const periodsByYear = new Map<string, PeriodOption[]>();
  for (const p of reference.periods) {
    const existing = periodsByYear.get(p.financialYearCode);
    if (existing) existing.push(p);
    else periodsByYear.set(p.financialYearCode, [p]);
  }

  const showPeriod = spec.params === 'period' || spec.params === 'periodAndCostCentre';
  const showYear = spec.params === 'year';
  const showCostCentre = spec.params === 'periodAndCostCentre';
  const showScope = spec.params === 'scope';

  return (
    <>
      <PageHeader
        title={provenance.reportName}
        subtitle={
          produced && provenance.entityName
            ? `${provenance.entityName}${provenance.parameters ? ` · ${provenance.parameters}` : ''}`
            : definition?.description
        }
        meta={
          produced ? (
            <>
              {provenance.runBy ? (
                <span>
                  Run by {provenance.runBy} at {provenance.runAtDisplay}
                </span>
              ) : null}
              {provenance.dataNotice ? <Badge tone="warn">{provenance.dataNotice}</Badge> : null}
            </>
          ) : null
        }
        actions={
          <Link className="btn" to="/reports">
            All reports
          </Link>
        }
      />

      {spec.params !== 'none' ? (
        <Card title="Report parameters">
          <div className="row">
            {showPeriod ? (
              <Select
                label="Accounting period"
                value={periodId ?? ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setPeriodId(value);
                  updateParam('periodId', value);
                }}
              >
                {[...periodsByYear.entries()].map(([year, periods]) => (
                  <optgroup key={year} label={year.replace('-', '/')}>
                    {periods.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            ) : null}

            {showScope ? (
              <Select
                label="Enquiry covers"
                value={scope === 'year' ? `year:${yearId ?? ''}` : `period:${periodId ?? ''}`}
                onChange={(e) => {
                  const [kind, id] = e.currentTarget.value.split(':');
                  if (kind === 'year') {
                    setScope('year');
                    setYearId(id ?? null);
                    updateParam('financialYearId', id ?? null);
                    updateParam('periodId', null);
                  } else {
                    setScope('period');
                    setPeriodId(id ?? null);
                    updateParam('periodId', id ?? null);
                    updateParam('financialYearId', null);
                  }
                }}
              >
                {[...periodsByYear.entries()].map(([year, periods]) => {
                  const yearOption = reference.years.find((y) => y.code === year);
                  return (
                    <optgroup key={year} label={year.replace('-', '/')}>
                      {yearOption ? (
                        <option value={`year:${yearOption.id}`}>Whole year to date, {yearOption.name}</option>
                      ) : null}
                      {periods.map((p) => (
                        <option key={p.id} value={`period:${p.id}`}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            ) : null}

            {showYear ? (
              <Select
                label="Financial year"
                value={yearId ?? ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setYearId(value);
                  updateParam('financialYearId', value);
                }}
              >
                {reference.years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </Select>
            ) : null}

            {showCostCentre ? (
              <Select
                label={t.costCentre}
                value={costCentreId ?? ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setCostCentreId(value);
                  updateParam('costCentreId', value);
                }}
              >
                {reference.costCentres.map((c) => (
                  // Indented with non-breaking spaces: a browser collapses
                  // ordinary leading whitespace inside an option, and the
                  // hierarchy is the point of the list.
                  <option key={c.id} value={c.id}>
                    {' '.repeat(c.depth * 3)}
                    {c.code} {c.name}
                  </option>
                ))}
              </Select>
            ) : null}

            {report.loading ? <span className="smaller muted">Running…</span> : null}
          </div>
        </Card>
      ) : null}

      <ErrorNotice error={exportCsv.error} />

      <AsyncPanel state={report}>
        {() =>
          view === null ? null : (
            <div className="stack">
              {view.stats && view.stats.length > 0 ? (
                <div className="grid grid--4">
                  {view.stats.map((stat) => (
                    <Stat key={stat.label} {...stat} />
                  ))}
                </div>
              ) : null}

              {view.notes}

              {view.sections.map((section) => (
                <SectionCard
                  key={section.key}
                  section={section}
                  reportName={provenance.reportName}
                  parameters={provenance.parameters}
                  canExport={can('report.export')}
                  busy={exportCsv.busy}
                  onExport={(payload) => void exportCsv.run(payload)}
                />
              ))}

              <p className="smaller muted">
                {provenance.dataNotice ? `${provenance.dataNotice} ` : ''}
                Produced{provenance.entityName ? ` for ${provenance.entityName}` : ''}
                {provenance.runBy ? ` by ${provenance.runBy}` : ''} at {provenance.runAtDisplay}
                {provenance.parameters ? ` (${provenance.parameters})` : ''}. Figures are computed when the report is
                run.
                {/* Only claimed where the report genuinely links; several
                    control registers have nothing to drill into. */}
                {view.sections.some((s) => s.columns.some((c) => c.linkTo))
                  ? ` Where a figure carries a link it opens the postings behind it, and from there the journal, the ${lower(
                      t.supplier,
                    )} invoice and the scanned document.`
                  : ''}
              </p>
            </div>
          )
        }
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * One table of a report
 * ------------------------------------------------------------------ */

function SectionCard({
  section,
  reportName,
  parameters,
  canExport,
  busy,
  onExport,
}: {
  section: Section;
  reportName: string;
  parameters: string | null;
  canExport: boolean;
  busy: boolean;
  onExport: (payload: Parameters<typeof api.exportCsv>[0]) => void;
}) {
  const emphasise = section.emphasise;
  const columns: Array<Column<Row>> = section.columns.map((col) => ({
    key: col.key,
    header: col.header,
    numeric: isNumericKind(col.kind),
    ...(col.width ? { width: col.width } : {}),
    ...(col.sortable ? { sortValue: (row: Row) => sortValueFor(row, col) } : {}),
    render: (row: Row) => renderCell(row, col, emphasise ? emphasise(row) : false),
  }));

  const total = section.total;
  // The label runs up to the first figure column so the totals line reads as
  // one sentence followed by the numbers it belongs to.
  const firstNumeric = section.columns.findIndex((c) => isNumericKind(c.kind));
  const labelSpan = firstNumeric > 0 ? firstNumeric : 1;

  const footer = total ? (
    <tr className="is-total">
      <td colSpan={labelSpan}>{total.label}</td>
      {section.columns.slice(labelSpan).map((col) => (
        <td key={col.key} className={isNumericKind(col.kind) ? 'num' : undefined}>
          {total.cells[col.key] ?? null}
        </td>
      ))}
    </tr>
  ) : undefined;

  const exportPayload = () => ({
    reportName: section.title === reportName ? reportName : `${reportName} — ${section.title}`,
    ...(parameters ? { parameters } : {}),
    columns: section.columns.map((c) => ({ key: c.key, label: c.header, type: exportTypeFor(c.kind) })),
    rows: section.rows.map((row) => {
      const out: Record<string, unknown> = {};
      for (const col of section.columns) out[col.key] = exportValue(row, col);
      return out;
    }),
    filename: `${slug(`${reportName} ${section.title}`)}-${todayIso()}.csv`,
  });

  return (
    <Card
      wide
      flush
      title={section.title}
      subtitle={section.subtitle}
      actions={
        canExport && section.rows.length > 0 ? (
          <button type="button" className="btn btn--sm" disabled={busy} onClick={() => onExport(exportPayload())}>
            {busy ? 'Exporting…' : 'Export to CSV'}
          </button>
        ) : null
      }
    >
      <DataTable
        caption={section.title}
        rows={section.rows}
        rowKey={(_row, index) => `${section.key}-${index}`}
        columns={columns}
        emptyMessage={section.empty ?? 'This report has no rows for the parameters you chose.'}
        {...(footer ? { footer } : {})}
        {...(section.searchable ? { searchValue: searchTextFor(section) } : {})}
        {...(section.pageSize ? { pageSize: section.pageSize } : {})}
      />
    </Card>
  );
}

/** What a row reads as, for the table's own search box. */
function searchTextFor(section: Section): (row: Row) => string {
  return (row) =>
    section.columns
      .filter((c) => !isNumericKind(c.kind))
      .map((c) => text(row[c.key]) ?? '')
      .join(' ');
}

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
